import graphqlService from "@/services/graphql.service";
import { uploadFileWithToken } from "@/services/uploadMedia.service";
import { buildQuery } from "@/graphql/builders/query.builder";
import { buildMutation } from "@/graphql/builders/mutation.builder";
// type_maps
import {updateUserTypeMap, userQueryTypeMap} from "@/graphql/type_maps/type.maps";

// ===== User mutations =====
/**
 * Update lang of user interface
 * @param {Object} params
 * @param {string} params.id
 * @param {string} params.interface_lang
 * @param fragment
 * @returns {Promise<{id: string, interface_lang: string}>}
 */
async function updateInterfaceLang({id, interface_lang}, fragment) {
    if (!id) throw new Error("updateInterfaceLang: id is required");

    const { query } = buildMutation({
        fragment,
        mutationName: 'UpdateUserLang',
        field: 'updateUser',
        variables: { id, interface_lang },
        typeMap: updateUserTypeMap,
    });

    const data = await graphqlService.requestGraphql({ query, variables: {id, interface_lang}});
    return data?.updateUser;
}

/**
 * Update profile of user
 * @param {Object} params
 * @param fragment
 * @param {string} params.id
 * @param {string=} params.profile
 * @param {string=} params.name
 * @param {string|null=} params.phone
 * @param {string=} params.password // optional
 * @returns {Promise<Object|null>}
 */
async function updateUserProfile({id, profile, name, phone, password}, fragment) {
    if (!id) throw new Error("updateUserProfile: id is required");

    const variables = {
        id,
        profile,
        name,
        phone,
        ...(password ? { password } : {}),
    };

    const { query } = buildMutation({
        fragment,
        mutationName: 'UpdateUser',
        field: 'updateUser',
        variables,
        typeMap: updateUserTypeMap,
    });
    const data = await graphqlService.requestGraphql({ query, variables});

    return data?.updateUser;
}

/**
 * Update Learning Center progress JSON for user.
 * @param {Object} params
 * @param {string} params.id
 * @param {Object} params.learning_center
 * @param fragment
 * @returns {Promise<Object|null>}
 */
async function updateUserLearningCenter({id, learning_center}, fragment) {
    if (!id) throw new Error("updateUserLearningCenter: id is required");

    const variables = {
        id,
        learning_center,
    };

    const { query } = buildMutation({
        fragment,
        mutationName: 'UpdateUserLearningCenter',
        field: 'updateUser',
        variables,
        typeMap: updateUserTypeMap,
    });

    const data = await graphqlService.requestGraphql({ query, variables });
    return data?.updateUser;
}

/**
 * Delete profile of user
 * @param {string} id
 * @param fragment
 * @returns {Promise<boolean>}
 */
async function deleteUser(id) {
    if (!id) throw new Error("deleteUser: id is required");

    const { query } = buildMutation({
        mutationName: 'DeleteUser',
        field: 'deleteUser',
        variables: { id },
        typeMap: updateUserTypeMap,
    });

    const data = await graphqlService.requestGraphql({ query, variables: {id}});
    return data?.deleteUser ?? false;
}

async function updateUserAvatar({ id, file, deleteAvatar = false }) {
    if (!id) throw new Error("updateUserAvatar: id is required");

    let uploadToken = null;
    if (!deleteAvatar) {
        if (!file) throw new Error("updateUserAvatar: file is required");
        ({ uploadToken } = await uploadFileWithToken({
            file,
            modelType: "user",
            field: "uploaded_avatar",
        }));
    }

    const query = `
        mutation updateUserAvatar($uploaded_avatar: String, $id: ID!) {
          updateUser(uploaded_avatar: $uploaded_avatar, id: $id) {
            id
            avatar
          }
        }
      `;

    const data = await graphqlService.requestGraphql({
        query,
        variables: { uploaded_avatar: uploadToken, id },
    });

    return data?.updateUser;
}

// ===== User queries =====
/**
 * Get one user by variables (builds query internally).
 * @param {Object} [params]
 * @param {string} params.fragment
 * @param {Object} [params.variables]
 * @param {string} [params.path]
 * @returns {Promise<Object|undefined>}
 */
async function getUserData(params = {}) {
    const { fragment, variables, path } = params;
    if (!fragment) throw new Error("getUserData: 'fragment' is required");

    const vars = variables ?? {}; // normalize null/undefined -> {}
    const { query } = buildQuery({
        fragment,
        field: "users",
        variables: vars,
        typeMap: userQueryTypeMap,
    });

    const data = await graphqlService.requestGraphql({ query, variables: vars, path });
    return Array.isArray(data?.users) ? data.users[0] : undefined;
}

/**
 * Get many users by variables (builds query internally).
 * @param {Object} [params]
 * @param {string} params.fragment
 * @param {Object} [params.variables]
 * @param {string} [params.path]
 * @returns {Promise<Array|undefined>}
 */
async function getUsersData(params = {}) {
    const { fragment, variables, path } = params;
    if (!fragment) throw new Error("getUsersData: 'fragment' is required");

    const vars = variables ?? {};
    const { query } = buildQuery({
        fragment,
        field: "users",
        variables: vars,
        typeMap: userQueryTypeMap,
    });

    const data = await graphqlService.requestGraphql({ query, variables: vars, path });
    return Array.isArray(data?.users) ? data.users : undefined;
}

export default {
    deleteUser,
    updateUserProfile,
    updateUserLearningCenter,
    updateInterfaceLang,
    updateUserAvatar,
    getUserData,
    getUsersData
};
